import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import { getActiveColorStyle } from "../../utils/colorUtils";
import { AiFillCaretLeft } from "react-icons/ai";
import { useNotification } from "contexts/NotificationContext";
import { getVehicleStatusDisplay } from "utils/vehicleStatusDisplay";

const THEME2_SCROLL_GAP_FILL_LIGHT = "#F3F4F8";
const THEME2_SCROLL_GAP_FILL_DARK = "#1E1E25";

const SubHeader = ({
  activeColor = "blue",
  title,
  back,
  /** When `back` is -1 and there is no in-app history (e.g. opened via direct link), navigate here instead of `navigate(-1)`. */
  backFallback,
  /** Overrides default `back` navigation when provided. */
  onBack,
  status,
  statusColor,
  right,
  link = true,
  /** Theme 2 desktop: align sticky stack with 56px navbar + 6px fill strip (inventory detail scroll bleed). */
  theme2ScrollGapFill = false,
  isLightTheme = false,
  /** Daha az alt boşluk (ör. Tenant Edit üst bar). */
  compact = false,
}) => {
  const resolvedActiveColor = theme2ScrollGapFill ? "custom" : activeColor;
  const activeColorStyle = getActiveColorStyle(resolvedActiveColor);
  const barSurfaceStyle = {
    background: activeColorStyle.backgroundImage,
    backgroundImage: activeColorStyle.backgroundImage,
    backgroundColor: activeColorStyle.borderColor,
  };
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const useTheme2Gap = theme2ScrollGapFill && !isMobile;
  const subHeaderMarginBottom = compact ? 6 : 12;
  const stickyTop = isMobile
    ? (theme2ScrollGapFill ? 53 : 54)
    : (theme2ScrollGapFill ? 50 : 62);
  const gapFillColor = isLightTheme ? THEME2_SCROLL_GAP_FILL_LIGHT : THEME2_SCROLL_GAP_FILL_DARK;

  // Helper to get status text and color
  const statusText = typeof status === 'object' ? (status?.status_name || status?.name) : status;
  const vehicleStatusDisplay =
    typeof status === "object" && status
      ? getVehicleStatusDisplay(status)
      : getVehicleStatusDisplay({ status_name: status });
  const finalStatusColor =
    statusColor ||
    vehicleStatusDisplay.color ||
    (typeof status === "object" ? status?.color : null);

  // Format status text by removing underscores and capitalizing each word
  const formatStatusText = (text) => {
    if (!text) return '';
    return text
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    if (back === -1) {
      const idx = window.history.state?.idx;
      if (typeof idx === "number" && idx > 0) {
        navigate(-1);
      } else if (backFallback) {
        navigate(backFallback);
      } else {
        navigate(-1);
      }
      return;
    }
    navigate(back);
  };

  const gradientOverlay = (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%)',
        pointerEvents: "none",
        zIndex: -1,
      }}
    />
  );

  const mainBarBody = (
    <>
      <div
        onClick={handleBack}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "32px",
          height: "32px",
          borderRadius: 0,
          marginRight: "10px",
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)"}
        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
      >
        <AiFillCaretLeft color="white" size={22} />
      </div>

      <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
        {title && (
          <span style={{ color: "white", fontSize: "16px" }}>
            {title}
          </span>
        )}

        <div className="d-flex align-items-center gap-3">
          {statusText && (
            <div style={{
              background: finalStatusColor || "rgba(255, 255, 255, 0.2)",
              padding: "2px 10px",
              color: "white",
              fontSize: "12px",
              fontWeight: "bold",
            }}>
              {formatStatusText(statusText)}
            </div>
          )}
          {link && (
          <Button
            color="link"
            size="sm"
            className="btn-icon btn-link btn-round dtiq-subheader-copy-link-btn"
            onClick={(e) => {
              e.stopPropagation();
              const href = `${window.location.origin}${window.location.pathname}`;
              navigator.clipboard.writeText(href);
              showNotification('success', 'Link copied!');
            }}
          >
            <i className="fas fa-link dtiq-subheader-copy-link-icon" />
          </Button>
          )}
          {right}
        </div>
      </div>
    </>
  );

  if (useTheme2Gap) {
    return (
      <div
        style={{
          position: "sticky",
          width: "100%",
          marginBottom: subHeaderMarginBottom,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          top: stickyTop,
        }}
      >
        <div
          aria-hidden
          style={{
            height: 6,
            flexShrink: 0,
            backgroundColor: gapFillColor,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "relative",
            height: 44,
            flexShrink: 0,
            ...barSurfaceStyle,
            display: "flex",
            alignItems: "center",
            padding: "0 10px",
          }}
        >
          {mainBarBody}
          {gradientOverlay}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: isMobile ? "fixed" : "sticky",
        left: isMobile ? 0 : undefined,
        right: isMobile ? 0 : undefined,
        width: '100%',
        height: "44px",
        ...barSurfaceStyle,
        top: stickyTop,
        marginBottom: isMobile ? 0 : subHeaderMarginBottom,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        padding: "0 10px",
      }}
    >
      {mainBarBody}

      {/* İsteğe bağlı olarak buraya bir overlay deseni veya amblem eklenebilir */}
      {gradientOverlay}
    </div>
  );
};

export default SubHeader;
